import { Container, TextField, Button, Typography, AppBar, Modal, Box, Toolbar, Paper, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Navigate, useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


function EditSection() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success');
  let navigate = useNavigate();
  const [sectionName, setSectionName] = useState('');
  const { sectionId } = useParams();
  const [loadingBarProgress, setLoadingBarProgress] = useState('');
  const [sections, setSections] = useState([]);
  const [error, setError] = useState(null);

  // Info dialog state
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${url}/Section/GetSection`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: sessionStorage.getItem('token'),
          },
        });
        if (!response.ok) {
          throw new Error(`Error fetching sections: ${response.status}`);
        }
        const data = await response.json();
        setSections(data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchSections();
  }, []);

  useEffect(() => {
    const section = sections.find(sec => sec.sectionId === parseInt(sectionId));
    if (section) {
      setSectionName(section.sectionName);
    } else {
      console.error('Section not found');
    }
  }, [sectionId, sections]);

  const handleCancel = () => {
    navigate(-1)
  }

  const handleUpdate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(0);
      const response = await fetch(`${apiUrl}/Section/Id?sectionId=${sectionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          sectionName: sectionName,
        }),
      });

      const data = await response.json();
      setLoadingBarProgress(0);

      const message = (data && (data.msg || data.message)) || 'Section has been updated successfully.';
      const status = (data && data.status && data.status.toString().toLowerCase()) || '';
      const lowerMsg = (message || '').toString().toLowerCase();
      const errorKeywords = ['already exist', 'already exists', 'failed', 'error', 'unable', 'cannot'];
      const messageHasError = status.includes('failed') || errorKeywords.some(k => lowerMsg.includes(k));

      setModalType(messageHasError ? 'error' : 'success');
      setModalMessage(message);
      setModalOpen(true);
    } catch (error) {
      setSnackbarMessage('API request error');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  return (
    <Container mt={4}>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Edit Section
          </Typography>

          {/* ── Info Button ── */}
          <Tooltip title="About this page" arrow>
            <IconButton
              onClick={() => setInfoOpen(true)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.75)",
                border: "1.5px solid rgba(255,255,255,0.35)",
                borderRadius: "50%",
                width: 30,
                height: 30,
                ml: 0.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#ffffff",
                  backgroundColor: "rgba(100,181,246,0.2)",
                  borderColor: "#64B5F6",
                  transform: "scale(1.1)",
                  boxShadow: "0 0 8px rgba(100,181,246,0.4)",
                },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} sx={{ padding: 2, width: '100%', margin: 'auto', marginTop: 1 }}>
        <div>
          <TextField
            label="Section Name"
            variant="outlined"
            fullWidth
            value={sectionName}
            onChange={(e) => setSectionName(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Tooltip title={!sectionName && "Enter Section"} arrow>
            <span><Button
              variant="contained"
              color="primary"
              onClick={handleUpdate}
              sx={{ mr: 2 }}
              disabled={!sectionName}
            >
              SAVE
            </Button></span></Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
          >
            CANCEL
          </Button>
        </div>
      </Paper>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="response-modal-title"
        aria-describedby="response-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: modalType === 'error' ? '#8B0000' : '#0B1F40', padding: '12px 16px' }}>
            <Typography id="response-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {modalType === 'error' ? 'Error ❌' : 'Section Updated ✅'}
            </Typography>
          </Box>

          <Box sx={{ padding: '20px' }}>
            <Typography id="response-modal-description" sx={{ mb: 2 }}>
              {modalMessage || (modalType === 'error' ? 'An error occurred.' : 'Section has been updated successfully.')}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setModalOpen(false);
                  if (modalType !== 'error') navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            boxShadow: "0 12px 40px rgba(11,31,61,0.2)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0B1F3D 0%, #1565C0 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 22 }} />
            Edit Section — Page Guide
          </Box>
          
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>

          {/* Purpose */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            Use this form to update the name of an existing section. The current section name
            is pre-filled automatically when the page loads. Make your changes and click Save
            to apply the update.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Field reference */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Field Reference
          </Typography>

          <List dense disablePadding>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <DriveFileRenameOutlineIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Section Name</Typography>}
                secondary="Pre-filled with the existing section name. Edit this field to set the new name. The Save button is disabled until the field contains a value."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Buttons */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Buttons
          </Typography>
          <List dense disablePadding>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <SaveOutlinedIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Save</Typography>}
                secondary="Submits the updated section name. Disabled when the field is empty. A response modal appears showing whether the update succeeded or failed."
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <ArrowBackIcon sx={{ fontSize: 20, color: "#d32f2f" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Cancel</Typography>}
                secondary="Navigates back to the previous page without saving any changes."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Response modal */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Response Feedback
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Box sx={{ px: 1.5, py: 0.6, backgroundColor: "#e3f2fd", borderRadius: "6px", border: "1px solid #90caf9" }}>
              <Typography variant="body2" fontWeight={600} color="#1565C0">✅ Success — section updated; clicking OK navigates back.</Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 0.6, backgroundColor: "#fce4ec", borderRadius: "6px", border: "1px solid #ef9a9a" }}>
              <Typography variant="body2" fontWeight={600} color="error.main">❌ Error — name already exists or server error; clicking OK stays on the page.</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Important notes */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Important Notes
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {[
              "The section name is pre-filled automatically from the existing record.",
              "Section Name must be unique — duplicate names will return an error.",
              "The Save button is disabled until the section name field has a value.",
              "On error the field retains its current value so you can correct and retry.",
              "Cancel navigates back immediately without any save or confirmation prompt.",
            ].map((note, i) => (
              <Typography key={i} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                {note}
              </Typography>
            ))}
          </Box>

        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#f5f5f5", px: 3, py: 2 }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#0B1F3D",
              color: "#fff",
              borderRadius: "8px",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#1565C0" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── End Info Dialog ── */}

    </Container>
  );
}

export default EditSection;